import { Button, Input, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { Fragment, useEffect, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { dateFormat, dateTimeFormat, fmtMoney, fmtNum, formatterPhone } from "@zhihaoscm/utils";
import { TAX_RATE } from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    MultiGrid as ZhMultiGrid
} from "zhihao-ui-react";

import request from "@/api";
import { useDetailBack } from "@/utils/hooks/useDetailBack";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import { useUserStore } from "@/views/stores";
import AssignDialog, { type assignSubmitData, type Officer } from "@/views/components/AssignDialog/AssignDialog";
import OperationHistoryPanel from "@/views/components/OperationHistoryPanel";
import { ProductDetailBankTable, ProductDetailMedia, ProductDetailRichText } from "./ProductDetailPrimitives";
import ProductDetailMapDialog from "./ProductDetailMapDialog";
import type { BargainRow } from "./types";
import { PROD_TAB } from "@/views/pages/tradeManage/goodsManagement/components";
import { fileService } from "@/api/services/fileService";

const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "未通过",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};
const bargainStateLabels: Record<number, string> = { 1: "待开始", 2: "捡漏中", 3: "已完成" };
const bargainWaitingPayLabels: Record<number, string> = {
    1: "30分钟",
    2: "60分钟",
    3: "12小时",
    4: "24小时",
    5: "48小时",
    6: "72小时"
};
const advertisingWatchLabels: Record<number, string> = { 1: "10秒", 2: "15秒", 3: "30秒", 4: "60秒" };
const payTypeLabels: Record<number, string> = { 1: "线上支付", 2: "线下支付", 3: "线上支付、线下支付" };

/** 将旧 Vue 详情的可选数字和单位保持为同一行文本。 */
function formatNumberWithUnit(value: number | undefined, unit: string): string {
    return value == null ? "--" : fmtNum(value, unit);
}

/** 捡漏详情金额沿用 Vue fmtMoney，固定两位小数并保留千分位。 */
function formatMoneyWithUnit(value: number | undefined, unit: string): string {
    return value == null ? "--" : fmtMoney(value, unit);
}

/** 保持旧 Vue 的提货周期展示，兼容接口只返回一端日期的情况。 */
function formatDeliveryPeriod(start?: string, end?: string): string {
    if (start && end) return `${dateFormat(start)} 至 ${dateFormat(end)}`;
    return (start && dateFormat(start)) || (end && dateFormat(end)) || "--";
}

/** 将旧 Vue 参数列表转换成 BaseItem 可直接渲染的文本。 */
function formatParameterInfo(parameterInfo?: BargainRow["productBargain"]["parameterInfo"]): ReactNode {
    if (!parameterInfo?.length) return "--";
    return (
        <div className="html-value product-detail-parameter-value">
            {parameterInfo.map((item, index) => (
                <Fragment key={`${item.parameterName || "parameter"}-${index}`}>
                    {`${item.parameterName || "--"}  ${item.parameterValue || "--"}`}
                    {index < parameterInfo.length - 1 ? <div className="con-width product-detail-con-width" /> : null}
                </Fragment>
            ))}
        </div>
    );
}

/** 将旧 Vue 标签数组转换成详情文本。 */
function formatTags(tags?: string[]): string {
    return tags?.length ? tags.join("、") : "--";
}

/** 将旧 Vue 品类及产区简称保持为列表相同的展示格式。 */
function formatProductType(productType?: BargainRow["productType"]): ReactNode {
    if (!productType?.name) return "--";
    return (
        <div className="html-value product-detail-type-value">
            {productType.name}
            {productType.areaAbbreviation ? <span className="split"> ({productType.areaAbbreviation})</span> : null}
        </div>
    );
}

/** 沿用旧 Vue 详情头的状态组合顺序，避免状态枚举迁移后出现语义变化。 */
function getBargainStateDescription(product: BargainRow["productBargain"]): string {
    return (
        [
            productStateLabels[product.state || 0],
            publishStateLabels[product.publishState || 0],
            bargainStateLabels[product.bargainState || 0]
        ]
            .filter(Boolean)
            .join(" | ") || "--"
    );
}

export interface BargainDetailPageProps {
    assignBargain?: (id: number | string, handlerId: number) => Promise<void>;
    auditBargain?: (id: number | string, payload: { state: boolean; auditDescn: string }) => Promise<void>;
    closeBargain?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfBargain?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (
        keyword: string
    ) => Promise<Array<{ user: { personId?: number; name?: string; employeeId?: string } }>>;
    loadBargain?: (id: number | string) => Promise<BargainRow>;
    permissions?: string[];
}

/** 复用 Vue 捡漏商品详情接口。 */
export async function loadBargainDetail(id: number | string): Promise<BargainRow> {
    const response = await request.service.get<BargainRow>(`/admin/product-bargain/vo/${id}`);
    return response.data;
}

/** 复用 Vue 捡漏商品审核接口。 */
export async function auditBargain(
    id: number | string,
    payload: { state: boolean; auditDescn: string }
): Promise<void> {
    await request.service.put(`/admin/product-bargain/audit/${id}`, payload);
}

/** 复用 Vue 捡漏商品下架接口。 */
export async function downShelfBargain(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-bargain/down-shelf/${id}`);
}

/** 复用 Vue 捡漏商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeBargain(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-bargain/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 捡漏商品指派接口。 */
export async function assignBargain(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-bargain/assign/${id}/${handlerId}`);
}

/** 复用 Vue 捡漏专员候选查询接口。 */
export async function findBargainSpecials(
    keyword: string
): Promise<Array<{ user: { personId?: number; name?: string; employeeId?: string } }>> {
    const response = await request.service.get<
        Array<{ user: { personId?: number; name?: string; employeeId?: string } }>
    >(`/admin/product-bargain/find/specials?name=${encodeURIComponent(keyword)}`);
    return response.data;
}

/** 将 Vue 捡漏详情的捡漏、商品和发布状态枚举统一为数字。 */
function normalizeBargainDetail(row: BargainRow): BargainRow {
    return {
        ...row,
        productBargain: {
            ...row.productBargain,
            bargainState: row.productBargain.bargainState == null ? undefined : Number(row.productBargain.bargainState),
            state: row.productBargain.state == null ? undefined : Number(row.productBargain.state),
            publishState: row.productBargain.publishState == null ? undefined : Number(row.productBargain.publishState)
        }
    };
}

/** 捡漏商品详情页，展示 Vue 详情页中的交易、提货、结算和服务字段。 */
export function BargainDetailPage({
    assignBargain: assign = assignBargain,
    auditBargain: submitAudit = auditBargain,
    closeBargain: close = closeBargain,
    downShelfBargain: removeFromShelf = downShelfBargain,
    findServiceSpecials: findSpecials = findBargainSpecials,
    loadBargain = loadBargainDetail,
    permissions
}: BargainDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const goBack = useDetailBack("/productManagement/bargain");
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const id = new URLSearchParams(location.search).get("id");
    const isAuditPage = location.pathname.endsWith("/audit");
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [detail, setDetail] = useState<BargainRow>();
    const [product, setProduct] = useState<BargainRow["productBargain"]>();
    const [advertisingFile, setAdvertisingFile] = useState<{ path?: string; type?: string }>();
    const [activeTab, setActiveTab] = useState(PROD_TAB.OVERVIEW);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [auditState, setAuditState] = useState<boolean | null>(null);
    const [auditDescn, setAuditDescn] = useState("");
    const [auditSubmitting, setAuditSubmitting] = useState(false);
    const [operationLogRefreshToken, setOperationLogRefreshToken] = useState(0);
    const [assignOpen, setAssignOpen] = useState(false);
    const [specialists, setSpecialists] = useState<Awaited<ReturnType<typeof findBargainSpecials>>>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);

    useEffect(() => {
        const previousTitle = document.title;
        document.title = `${isAuditPage ? "审核捡漏商品" : "捡漏商品详情"} - 链云砂石`;
        return () => {
            document.title = previousTitle;
        };
    }, [isAuditPage]);

    useEffect(() => {
        if (!id) return;
        void loadBargain(id)
            .then(result => {
                const normalizedResult = normalizeBargainDetail(result);
                setErrorMessage(null);
                setDetail(normalizedResult);
                setProduct(normalizedResult.productBargain);
                setAdvertisingFile(undefined);
                const advertIds = normalizedResult.productBargain.advertFileIds || [];
                if (advertIds.length) {
                    void fileService
                        .getFileByIds(advertIds)
                        .then(files => setAdvertisingFile(files[0]))
                        .catch(() => setAdvertisingFile(undefined));
                }
            })
            .catch(() => setErrorMessage("捡漏商品详情加载失败"));
    }, [id, loadBargain]);

    const refreshDetail = async () => {
        if (!id) return;
        const normalizedResult = normalizeBargainDetail(await loadBargain(id));
        setDetail(normalizedResult);
        setProduct(normalizedResult.productBargain);
    };

    const handleClose = async () => {
        if (!id || !detail) return;
        const unActiveFileIds = [
            detail.mainFile?.id,
            detail.videoFile?.id,
            ...(detail.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            navigate("/productManagement/bargain");
        } catch {
            // 关闭接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("捡漏商品关闭失败");
        }
    };

    const handleDownShelf = async () => {
        if (!id) return;
        try {
            await removeFromShelf(id);
            await refreshDetail();
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            // 下架或刷新接口失败时保留当前详情，并反馈与 Vue 页面一致的失败状态。
            setErrorMessage("捡漏商品下架失败");
        }
    };

    const handleAudit = async () => {
        if (!id || auditState == null) return;
        if (!auditState && !auditDescn.trim()) {
            setErrorMessage("请填写驳回原因");
            return;
        }
        setAuditSubmitting(true);
        try {
            await submitAudit(id, { state: auditState, auditDescn: auditDescn.trim() });
            navigate("/productManagement/bargain");
        } catch {
            setErrorMessage("捡漏商品审核失败");
        } finally {
            setAuditSubmitting(false);
        }
    };

    /** 点击审核结果后打开意见弹窗，避免详情页出现额外的页内提交区域。 */
    const openAuditDialog = (state: boolean) => {
        setAuditState(state);
        setAuditDescn("");
        setErrorMessage(null);
    };

    /** 查询可指派的商品专员并沿用旧版弹窗的候选列表。 */
    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            setSpecialists(await findSpecials(keyword));
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    /** 打开指派弹窗，并默认选中当前商品专员。 */
    const openAssign = () => {
        if (!product) return;
        setAssignOpen(true);
        setSelectedHandlerId(product.handlerId);
        void searchSpecialists("");
    };

    /** 提交商品专员变更，并刷新详情和操作日志。 */
    const handleAssign = async ({ officerId }: assignSubmitData) => {
        if (!id || officerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        setAssignLoading(true);
        try {
            await assign(id, officerId);
            setAssignOpen(false);
            await refreshDetail();
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setAssignError("变更指派失败");
        } finally {
            setAssignLoading(false);
        }
    };

    return (
        <section aria-label="bargain-detail-page" className="detail-page-transparent admin-detail-page">
            <AssignDialog
                open={assignOpen}
                info={detail || undefined}
                title="变更指派商品专员"
                officers={specialists as Officer[]}
                searchSpecial={async keyword => {
                    await searchSpecialists(keyword);
                    return [];
                }}
                tips="跟进的商品数量"
                countFiled="productCount"
                countSuffix="个"
                curOfficerId={selectedHandlerId}
                confirmText="确认"
                loading={assignLoading}
                error={assignError}
                onClose={() => {
                    setAssignOpen(false);
                    setAssignError(null);
                }}
                onCurOfficerIdChange={setSelectedHandlerId}
                onSubmit={handleAssign}
            />
            <div className="section-card admin-detail-header-card">
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {product ? (
                    <>
                        <ZhDetailHeader
                            leftInfos={[
                                { label: "商品编号", value: product.id || "--" },
                                {
                                    label: "商品专员",
                                    value: (
                                        <>
                                            {product.handlerName || "--"}
                                            {product.handlerMobile?.length
                                                ? ` | ${formatterPhone(product.handlerMobile[0])}`
                                                : ""}
                                        </>
                                    )
                                }
                            ]}
                            stateDesc={getBargainStateDescription(product)}
                            title={isAuditPage ? "审核捡漏商品" : "捡漏商品详情"}
                        />
                        {product.bargainState === 2 ? (
                            <div className="bargain-detail-tabs" role="tablist" aria-label="捡漏商品页签">
                                <button
                                    aria-selected={activeTab === PROD_TAB.OVERVIEW}
                                    className={activeTab === PROD_TAB.OVERVIEW ? "active" : ""}
                                    role="tab"
                                    type="button"
                                    onClick={() => setActiveTab(PROD_TAB.OVERVIEW)}
                                >
                                    商品概览
                                </button>
                                <button
                                    aria-selected={activeTab === PROD_TAB.HELP_DETAIL}
                                    className={activeTab === PROD_TAB.HELP_DETAIL ? "active" : ""}
                                    role="tab"
                                    type="button"
                                    onClick={() => setActiveTab(PROD_TAB.HELP_DETAIL)}
                                >
                                    助力明细
                                </button>
                                <span aria-hidden="true" />
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
            {product && activeTab === PROD_TAB.OVERVIEW ? (
                <ZhMultiGrid>
                    <section aria-label="商品信息" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">商品信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem colon={false} label="商品标题" labelWidth={56} value={product.title || "--"} />
                            <ZhBaseItem colon={false} label="权重值" labelWidth={112} value={product.weight ?? "--"} />
                            <ZhBaseItem
                                label="捡漏单价"
                                labelWidth={84}
                                value={formatMoneyWithUnit(product.bargainUnitPrice, "元/吨")}
                            />
                            <ZhBaseItem
                                label="是否含税"
                                labelWidth={84}
                                value={
                                    product.isIncludeTax === 1
                                        ? `含税(${TAX_RATE.getIntroByCode(product.taxRate)})`
                                        : product.isIncludeTax === 0
                                          ? "不含税"
                                          : "--"
                                }
                            />
                            <ZhBaseItem
                                label="市场价"
                                labelWidth={56}
                                value={formatMoneyWithUnit(product.unitPrice, "元/吨")}
                            />
                            <ZhBaseItem
                                label="捡漏吨数"
                                labelWidth={112}
                                value={formatNumberWithUnit(product.bargainTon, "吨")}
                            />
                            <ZhBaseItem
                                label="总价"
                                labelWidth={84}
                                value={formatMoneyWithUnit(product.totalCost, "元")}
                            />
                            <ZhBaseItem
                                label="捡漏开始时间"
                                labelWidth={84}
                                value={dateTimeFormat(product.bargainStartTime)}
                            />
                            <ZhBaseItem
                                label="活动天数"
                                labelWidth={56}
                                value={formatNumberWithUnit(product.bargainCycle, "天")}
                            />
                            <ZhBaseItem
                                label="捡漏截止时间"
                                labelWidth={112}
                                value={dateTimeFormat(product.bargainEndTime)}
                            />
                            <ZhBaseItem
                                label="等待付款时间"
                                labelWidth={84}
                                value={bargainWaitingPayLabels[product.waitingPayTime || 0] || "--"}
                            />
                            <ZhBaseItem
                                label="广告观看时间"
                                labelWidth={84}
                                value={advertisingWatchLabels[product.advertisingWatchTime || 0] || "--"}
                            />
                            <ProductDetailMedia
                                imagePath={detail?.mainFile?.path}
                                videoPath={detail?.videoFile?.path}
                                advertisingPath={advertisingFile?.path}
                                advertisingIsVideo={advertisingFile?.type?.toLowerCase().includes("mp4")}
                            />
                            <ZhBaseItem
                                label="供应商"
                                labelWidth={56}
                                value={
                                    detail?.productBargain.supplierEnterprise?.enterpriseName ||
                                    detail?.productBargain.supplierEnterprise?.supplierName ||
                                    detail?.productBargain.supplierEnterprise?.name ||
                                    "--"
                                }
                            />
                            <ZhBaseItem label="品类" labelWidth={112} value={formatProductType(detail?.productType)} />
                            <ZhBaseItem label="规格" labelWidth={84} value={product.productTypeSpec || "--"} />
                            <ZhBaseItem
                                label="品类标签"
                                labelWidth={84}
                                value={formatTags(
                                    detail?.productTypeVo?.tags?.map(tag => tag.name).filter(Boolean) as string[]
                                )}
                            />
                            <ZhBaseItem
                                label="参数值"
                                labelWidth={56}
                                span={4}
                                value={formatParameterInfo(product.parameterInfo)}
                            />
                            <ZhBaseItem
                                label="商品标签"
                                labelWidth={56}
                                span={4}
                                value={formatTags(product.productLabel)}
                            />
                            <ZhBaseItem
                                label="商品介绍"
                                labelWidth={56}
                                span={4}
                                value={<ProductDetailRichText value={product.introduce} />}
                            />
                        </ZhGrid>
                    </section>
                    <section aria-label="提货信息" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">提货信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem
                                label="提货周期"
                                labelWidth={56}
                                value={formatDeliveryPeriod(product.deliveryStartTime, product.deliveryEndTime)}
                            />
                            <ProductDetailMapDialog location={product} />
                            <ZhBaseItem
                                label="提货说明"
                                labelWidth={56}
                                span={4}
                                value={<ProductDetailRichText value={product.deliveryDescn} />}
                            />
                        </ZhGrid>
                    </section>
                    <section aria-label="结算信息" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">结算信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem
                                label="支付方式"
                                labelWidth={84}
                                value={payTypeLabels[product.payType || 0] || "--"}
                            />
                            <ZhBaseItem
                                label="结算说明"
                                labelWidth={56}
                                span={4}
                                value={<ProductDetailRichText value={product.settlementDescn} />}
                            />
                        </ZhGrid>
                        <ProductDetailBankTable accounts={product.accountInfo} payType={product.payType} />
                    </section>
                    <section aria-label="服务保障" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">服务保障</div>
                        <ZhGrid cols={1} gap={[20, 20]}>
                            <ZhBaseItem
                                label="服务保障"
                                labelWidth={56}
                                span={4}
                                value={<ProductDetailRichText value={product.serviceGuarantee} />}
                            />
                            <ZhBaseItem
                                label="捡漏规则"
                                labelWidth={56}
                                span={4}
                                value={<ProductDetailRichText value={product.regulation} />}
                            />
                            {product.publishState === 4 ? (
                                <ZhBaseItem
                                    label="驳回原因"
                                    labelWidth={56}
                                    span={4}
                                    value={product.auditDescn || "--"}
                                />
                            ) : null}
                        </ZhGrid>
                    </section>
                    <section aria-label="其他信息" className="section-card admin-detail-section product-detail-section">
                        <div className="section-title mb-16">其他信息</div>
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="商品专员" labelWidth={56} value={product.handlerName || "--"} />
                            <ZhBaseItem
                                label="商品专员联系电话"
                                labelWidth={112}
                                span={3}
                                value={product.handlerMobile?.map(formatterPhone).join("; ") || "--"}
                            />
                        </ZhGrid>
                    </section>
                </ZhMultiGrid>
            ) : null}
            {detail ? (
                <OperationHistoryPanel
                    bizNo={id || detail.productBargain.id || ""}
                    module={Operation_History_Enum.PRODUCT_BARGAIN_DETAIL}
                    refreshToken={operationLogRefreshToken}
                />
            ) : null}
            <Space className="operation-fixed-btn" size={0} wrap>
                {isAuditPage ? (
                    <>
                        <Button aria-label="同意" onClick={() => openAuditDialog(true)}>
                            同意
                        </Button>
                        <Button aria-label="拒绝" onClick={() => openAuditDialog(false)}>
                            拒绝
                        </Button>
                    </>
                ) : detail ? (
                    <>
                        {canManage &&
                        product &&
                        !(product.state === 2 && [4, 5].includes(product.publishState || 0)) ? (
                            <Button aria-label="变更指派" onClick={openAssign}>
                                变更指派
                            </Button>
                        ) : null}
                        {canManage && product?.publishState === 1 ? (
                            <Button
                                aria-label="审核捡漏商品"
                                onClick={() => void navigate(`/productManagement/bargain/audit?id=${id || ""}`)}
                            >
                                审核
                            </Button>
                        ) : null}
                        {canDeal && product?.state === 2 && [1, 2, 4, 5, 6].includes(product.publishState || 0) ? (
                            <Button
                                aria-label="编辑捡漏商品"
                                onClick={() => void navigate(`/productManagement/bargain/edit?id=${id || ""}`)}
                            >
                                编辑
                            </Button>
                        ) : null}
                        {canClosePermission &&
                        product?.state === 2 &&
                        [1, 2, 4, 5, 6].includes(product.publishState || 0) ? (
                            <Button aria-label="关闭捡漏商品" onClick={() => void handleClose()}>
                                关闭
                            </Button>
                        ) : null}
                        {canDeal && product?.state === 1 && [2, 3].includes(product.publishState || 0) ? (
                            <Button aria-label="下架捡漏商品" onClick={() => void handleDownShelf()}>
                                下架
                            </Button>
                        ) : null}
                        {canDeal && product?.state === 2 && product.publishState === 5 && product.bargainState === 3 ? (
                            <Button
                                aria-label="复制捡漏商品"
                                onClick={() => void navigate(`/productManagement/bargain/add?copyId=${id || ""}`)}
                            >
                                复制
                            </Button>
                        ) : null}
                    </>
                ) : null}
                <Button onClick={goBack}>返回</Button>
            </Space>
            {isAuditPage ? (
                <ZhDialog
                    open={auditState != null}
                    title={auditState ? "审核意见" : "驳回原因"}
                    width={400}
                    onCancel={() => setAuditState(null)}
                    footer={
                        <Space>
                            <Button onClick={() => setAuditState(null)}>取消</Button>
                            <Button
                                aria-label="确认"
                                loading={auditSubmitting}
                                type="primary"
                                onClick={() => void handleAudit()}
                            >
                                确认
                            </Button>
                        </Space>
                    }
                >
                    {errorMessage === "请填写驳回原因" ? (
                        <Alert message={errorMessage} role="alert" type="error" />
                    ) : null}
                    <Input.TextArea
                        aria-label={auditState ? "审核意见" : "驳回原因"}
                        maxLength={200}
                        placeholder={auditState ? "请输入审核意见" : "请输入驳回原因"}
                        rows={4}
                        value={auditDescn}
                        onChange={event => setAuditDescn(event.target.value)}
                    />
                </ZhDialog>
            ) : null}
        </section>
    );
}

export default BargainDetailPage;
